import {
  AcceptableInput,
  BackendApi,
  CommonVerifierFeedback,
  CommonVerifierFeedback as Feedback,
  DocumentCodes,
  DocumentFilter,
  DocumentVerifierSettings,
  IVerifier,
  MsLivenessCreateSessionResponse,
  MsLivenessVerifierSettings,
  OnFrameProcessedCallback,
  PageCodes,
  SampleToUpload,
  SampleType,
  SdkVerifierType,
  SetupInfo,
  SupportedLanguages,
  UploadSampleBasicInfo,
  VerifierState,
  VerifierStateContainerForPublicDataBase,
  ZenidWebController,
} from "@zenid/websdk";
import { useEffect, useRef, useState } from "react";
import { ReaderTaskEither } from "fp-ts/ReaderTaskEither";
import { NonEmptyString } from "io-ts-types/NonEmptyString";
import { constant, flow, pipe } from "fp-ts/function";
import { array, either, eq, option, task, taskEither } from "fp-ts";
import { Either } from "fp-ts/Either";
import { Option } from "fp-ts/Option";
import { useFormatMessage } from "../../intl";
import {
  APIParameters,
  DocumentIdentificationFlow,
  foldTenant,
  Tenant,
} from "../../globalDomain";
import { useAppContext } from "../../useAppContext";
import { useCommand } from "../../useAPI";
import * as uploadApi from "../api";

export type CameraError = "unavailable" | "disabled" | "generic";
type ZenidError =
  | "InitializationFailed"
  | "InvalidToken"
  | "LoadFeatureFailed"
  | "CameraTestFailed"
  | "StartCameraFailed"
  | void;

export type SampleData = {
  sampleType: SampleType;
  documentCode?: DocumentCodes;
  pageCode?: PageCodes;
  hasOtherSide?: boolean;
};

const validateToken = (token: string): Either<ZenidError, NonEmptyString> =>
  pipe(
    token,
    NonEmptyString.decode,
    either.mapLeft<unknown, ZenidError>(constant("InvalidToken"))
  );

export const useZenid2 = (
  exchangeTokenCommand: ReaderTaskEither<
    { token: NonEmptyString },
    unknown,
    { token: NonEmptyString }
  >,
  uploadSample: (
    sampleToUpload: SampleToUpload,
    sampleData: SampleData
  ) => Promise<UploadSampleBasicInfo>,
  supportedDocuments?: DocumentCodes[],
  allowedSdkVerifiers?: SdkVerifierType[],
  autoInit = true
) => {
  const context = useAppContext();
  const msSessionCreate = useCommand(uploadApi.msLivenessCreateSession);
  const msSessionResult = useCommand(uploadApi.msLivenessSessionResult);
  const msSessionImage = useCommand(uploadApi.msLivenessSessionImage);
  const getProfile = useCommand(uploadApi.getProfileId);

  const [initialized, setInitialized] = useState(false);
  const [cameraError, setCameraError] = useState<Option<CameraError>>(
    option.none
  );
  const isStartingCamera = useRef(false);
  const [feedback, setFeedback] = useState<Option<CommonVerifierFeedback>>(
    option.none
  );

  const sampleType = useRef<SampleType | undefined>(undefined);
  const sampleData = useRef<SampleData | undefined>(undefined);
  const tokenZen = useRef<string | undefined>(undefined);

  const getLanguage = (
    language: APIParameters["language"]
  ): SupportedLanguages => {
    switch (language) {
      case "cs":
        return SupportedLanguages.Czech;
      case "en":
        return SupportedLanguages.English;
      case "sk":
        return SupportedLanguages.English;
    }
  };

  const initialize = () => {
    /*const backendApi = new MyZenidBackendApi("https://ucbzenid.trask.cz:8001", (token: string): Promise<string> => {
      return pipe(
          token,
          validateToken,
          taskEither.fromEither,
          taskEither.chain(token => exchangeTokenCommand({ token })),
          taskEither.fold(
              e => constant(Promise.reject(e)),
              ({ token: exchangedToken }) => {
                tokenZen.current = exchangedToken;
                return task.of(exchangedToken);
              }
          )
      )()}, sample => {
        if (sampleData.current) {
          if (sampleType.current) {
            sampleData.current.sampleType = sampleType.current;
          }
          return uploadSample(sample, sampleData.current);
        } else {
          return Promise.reject("No sample type known");
        }
      }, () => task.of({
        AllowedDocumentCodes: supportedDocuments,
        AllowedSdkVerifiers: allowedSdkVerifiers,
      })());*/
    const backendApi: BackendApi = {
      getBackendUrl: () => "",
      uploadSample: sample => {
        if (sampleData.current) {
          if (sampleType.current) {
            sampleData.current.sampleType = sampleType.current;
          }
          return uploadSample(sample, sampleData.current);
        } else {
          return Promise.reject("No sample type known");
        }
      },
      initSdk: (token: string): Promise<string> => {
        return pipe(
          token,
          validateToken,
          taskEither.fromEither,
          taskEither.chain(token => exchangeTokenCommand({ token })),
          taskEither.fold(
            e => constant(Promise.reject(e)),
            ({ token: exchangedToken }) => {
              tokenZen.current = exchangedToken;
              return task.of(exchangedToken);
            }
          )
        )();
      },
      getSample(_: string): Promise<UploadSampleBasicInfo> {
        return Promise.reject();
      },
      getSetupInfo(): Promise<SetupInfo> {
        return task.of({
          AllowedDocumentCodes: supportedDocuments,
          AllowedSdkVerifiers: allowedSdkVerifiers,
          Profiles: [
            "Profile_FACE_ID_Inperson_Mobile",
            "Profile_FACE_ID_Inperson_Mobile_Foreigners",
            "Profile_FACE_ID_Inperson_Scanner",
            "Profile_NO_FACE_ID_Inperson_Scanner",
            "Profile_FACE_ID",
            "Profile_NO_FACE_ID_Inperson_Mobile",
            "Profile_Selfie_Liveness",
            "Profile_FACE_ID_Remote_Onboarding",
            "Profile_NO_FACE_ID_Remote",
            "Profile_NO_FACE_ID_Remote_Onboarding",
            "Profile_NO_FACE_ID_Inperson_Scanner_Foreigners",
            "Profile_hologram",
            "Profile_FACE_ID_Inperson_Scanner_Foreigners",
            "UCBProfile1",
            "Profile_Slovakia_Remote",
            "Profile_photo_match_and_hologram",
            "Profile_NO_FACE_ID",
            "Profile_NO_FACE_ID_Inperson_Mobile_Foreigners",
            "Profile_photo_match",
            "Profile_FACE_ID_Remote",
          ],
        })();
      },
      investigateSamples(_: string[], __: boolean): Promise<any> {
        return Promise.reject();
      },
      msLivenessCreateSession(
        deviceCorrelationId: string
      ): Promise<MsLivenessCreateSessionResponse> {
        return pipe(
          msSessionCreate({ DeviceCorrelationId: deviceCorrelationId }),
          taskEither.fold(
            e => constant(Promise.reject(e)),
            resp => task.of(resp as MsLivenessCreateSessionResponse)
          )
        )();
      },
      msLivenessGetSessionResult(sessionId: string): Promise<any> {
        return pipe(
          msSessionResult({ sessionId: sessionId }),
          taskEither.fold(
            e => constant(Promise.reject(e)),
            resp => {
              // @ts-ignore
              return task.of(JSON.parse(resp["ResultJson"] as string));
            }
          )
        )();
      },
      msLivenessGetSessionImage(sessionImageId: string): Promise<string> {
        return pipe(
          msSessionImage({ sessionImageId: sessionImageId }),
          taskEither.fold(
            e => constant(Promise.reject(e)),
            resp => {
              // @ts-ignore
              return task.of(resp["ImageBase64"] as string);
            }
          )
        )();
      },
    };

    // const modelsUrl = new URL("./zenid/models", window.location.href).href;
    return pipe(
      taskEither.fromTask(() =>
        ZenidWebController.Instance.init(backendApi, "", false, true)
      ),
      taskEither.chain(() =>
        taskEither.fromTask(() =>
          ZenidWebController.Instance.setLanguage(
            getLanguage(context.apiParameters.language)
          )
        )
      ),
      taskEither.chain(() =>
        taskEither.fromTask(ZenidWebController.Instance.cameraPermissionDenied)
      ),
      taskEither.chain(cameraDenied =>
        taskEither.fromIO(() => {
          // ZenidWebController.Instance.
          // ZenidWebController.Instance.enableDebugVisualization(true);
          setCameraError(cameraDenied ? option.some("disabled") : option.none);
          setInitialized(!cameraDenied);
        })
      )
    );
  };
  useEffect(() => {
    if (autoInit) {
      initialize()();
    }
    return () => {
      ZenidWebController.Instance.stop();
    };
  }, []);

  const startCamera = () => {
    /*    setIsStartingCamera(true);
    pipe(
      ZenidWebController.Instance.camera.start,
      taskEither.fromTask,
      taskEither.bimap(
        () => setCameraError(option.some("generic")),
        () => {
          setCameraError(option.none);
          setIsStartingCamera(false);
        }
      )
    )();*/
  };
  const onFrameProcessedCallback: OnFrameProcessedCallback = (
    state: VerifierStateContainerForPublicDataBase,
    verif: IVerifier
  ) => {
    isStartingCamera.current = false;
    if (state.State === VerifierState.Success && sampleType.current) {
      console.log(">>> onFrameProcessedCallback", state, verif);
      sampleData.current = undefined;
      if ("DocumentCode" in state && "PageCode" in state) {
        sampleData.current = {
          sampleType: sampleType.current,
          documentCode: state["DocumentCode"],
          pageCode: state["PageCode"],
          hasOtherSide:
            "SupportedPageCount" in state
              ? (state["SupportedPageCount"] as number) > 1
              : undefined,
        };
      } else if (sampleType.current === SampleType.Selfie) {
        sampleData.current = {
          sampleType: SampleType.Selfie,
        };
      }
      /* if (state instanceof DocumentVerifierStateContainerForPublicData) {
          if (state.DocumentCode && state.PageCode) {
            sampleData.current = {
              sampleType: SampleType.DocumentPicture,
              documentCode: state.DocumentCode,
              pageCode: state.PageCode,
            };
          }
        } else if (state instanceof HologramStateContainerForPublicData) {
          sampleData.current = {
            sampleType: SampleType.DocumentVideo,
            documentCode: state.DocumentCode,
            pageCode: state.PageCode,
          };
        } else if (state instanceof MsLivenessStateContainerForPublicData) {
          sampleData.current = {
            sampleType: SampleType.SelfieVideo,
          };
        }*/
      console.log(">>> onFrameProcessedCallback", sampleData);
    }
    pipe(
      feedback,
      option.fold(
        () => setFeedback(option.fromNullable(state.Feedback)),
        currentFeedback =>
          currentFeedback !== state.Feedback &&
          setFeedback(option.fromNullable(state.Feedback))
      )
    );
  };

  const doProfileTask = (sample: SampleType) =>
    flow(
      () => {
        isStartingCamera.current = true;
        sampleType.current = sample;
      },
      () => getProfile(option.none),
      taskEither.mapLeft(() => setCameraError(option.some("generic"))),
      taskEither.chain(profile =>
        taskEither.fromTask(() =>
          ZenidWebController.Instance.selectProfile(profile.profileId)
        )
      )
    )();

  const startScanningDocument = (
    documentFilter: DocumentFilter[] | undefined
  ) =>
    pipe(
      doProfileTask(SampleType.DocumentPicture),
      taskEither.chain(() =>
        taskEither.fromTask(() =>
          ZenidWebController.Instance.verifyAndUpload(
            SdkVerifierType.Document,
            {
              DrawOutline: false,
              AcceptableInput: { PossibleDocuments: documentFilter },
              ShowTimer: true,
            } as DocumentVerifierSettings,
            onFrameProcessedCallback
          )
        )
      )
    )();

  const startScanningLiveness = () =>
    pipe(
      doProfileTask(SampleType.Selfie),
      taskEither.chain(() =>
        taskEither.fromTask(() =>
          ZenidWebController.Instance.verifyAndUpload(
            SdkVerifierType.MsLiveness,
            {} as MsLivenessVerifierSettings,
            onFrameProcessedCallback
          )
        )
      )
    )();

  const startScanningHologram = () =>
    pipe(
      doProfileTask(SampleType.DocumentVideo),
      taskEither.chain(() =>
        taskEither.fromTask(() =>
          ZenidWebController.Instance.verifyAndUpload(
            SdkVerifierType.Hologram,
            {} as DocumentVerifierSettings,
            onFrameProcessedCallback
          )
        )
      )
    )();

  return {
    initialize,
    initialized,
    cameraError,
    startCamera,
    isStartingCamera: isStartingCamera.current,
    startScanningDocument,
    startScanningLiveness,
    startScanningHologram,
    feedback,
  };
};

export type AcquiredDocument = {
  _tag: "Document";
  blob: Blob;
  documentCode: DocumentCodes;
  pageCode: PageCodes;
  hasOtherSide: boolean;
  signature: string;
};

export type AcquiredDocumentVideo = {
  _tag: "DocumentVideo";
  blob: Blob;
  documentCode: DocumentCodes;
  pageCode: PageCodes;
  signature: string;
};

export type AcquiredSelfie = {
  _tag: "Selfie";
  blob: Blob;
  signature: string;
};

const idCardCodesCZ: DocumentCodes[] = [DocumentCodes.IDC2];
const passportCodesCZ: DocumentCodes[] = [
  DocumentCodes.PAS,
  DocumentCodes.CZ_PAS_V2,
];
const drivingLicenseCodesCZ: DocumentCodes[] = [DocumentCodes.DRV];
// const residencePermitCodesCZ: DocumentCode[] = [DocumentCode.CZ_RES_2006_T, DocumentCode.CZ_RES_2006_07, DocumentCode.CZ_RES_2011_14];
// ^^ Could be useful in future ^^
const passportCodesSK: DocumentCodes[] = [
  DocumentCodes.SK_PAS_2008_14,
  DocumentCodes.SK_PAS_2024,
];
const drivingLicenseCodesSK: DocumentCodes[] = [
  DocumentCodes.SK_DRV_2004_08_09,
  DocumentCodes.SK_DRV_2013,
  DocumentCodes.SK_DRV_2015,
];
const idCardCodesSK: DocumentCodes[] = [
  DocumentCodes.SK_IDC_2008plus,
  DocumentCodes.SK_IDC_2022,
  DocumentCodes.SK_IDC_2024,
];

export const getDocumentCodes = (
  tenant: Tenant,
  isRemote: boolean,
  isPersonalProfile: boolean,
  documentIdentificationFlow: DocumentIdentificationFlow
) => {
  return foldTenant(
    tenant,
    () => {
      switch (documentIdentificationFlow) {
        case "PrimaryAndLivenessCheck":
        case "Primary":
          return isRemote && !isPersonalProfile
            ? [...idCardCodesSK]
            : [...idCardCodesSK, ...passportCodesCZ];
        case "Secondary":
          return !isPersonalProfile
            ? []
            : [
                ...passportCodesSK,
                ...drivingLicenseCodesSK,
                ...drivingLicenseCodesCZ,
              ];
        case "PrimaryAndSecondary":
          return [
            ...idCardCodesSK,
            ...passportCodesCZ,
            ...passportCodesSK,
            ...drivingLicenseCodesSK,
            ...drivingLicenseCodesCZ,
          ];
      }
    },
    () => {
      switch (documentIdentificationFlow) {
        case "PrimaryAndLivenessCheck":
        case "Primary":
          return !isPersonalProfile && isRemote
            ? [...idCardCodesCZ]
            : [...idCardCodesCZ, ...passportCodesSK];
        case "Secondary":
          return isPersonalProfile
            ? [
                ...passportCodesCZ,
                ...drivingLicenseCodesCZ,
                ...drivingLicenseCodesSK,
              ]
            : isRemote
            ? [...passportCodesCZ, ...drivingLicenseCodesCZ]
            : [];
        case "PrimaryAndSecondary":
          return [
            ...idCardCodesCZ,
            ...passportCodesSK,
            ...passportCodesCZ,
            ...drivingLicenseCodesCZ,
            ...drivingLicenseCodesSK,
          ];
      }
    }
  );
};

export function useFormatFeedback(acceptableInput?: Option<AcceptableInput>) {
  const formatMessage = useFormatMessage();

  function isArrayOfDocumentAcceptable(documentCodes: DocumentCodes[]) {
    return (acceptableInput: DocumentFilter[] | undefined): boolean =>
      !!acceptableInput &&
      pipe(
        acceptableInput,
        array.difference(
          eq.fromEquals(
            (a: DocumentFilter, b: DocumentFilter) =>
              a.DocumentCode === b.DocumentCode
          )
        )(
          pipe(
            documentCodes,
            array.map<DocumentCodes, DocumentFilter>(dc => ({
              DocumentCode: dc,
            }))
          )
        )
      ).length === 0;
  }

  function formatFeedback(feedback: Feedback) {
    switch (feedback) {
      case Feedback.Ok:
        return formatMessage("Zenid.Feedback.ok");
      case Feedback.DocumentNoMatchFound:
        return pipe(
          option.fromNullable(acceptableInput),
          option.flatten,
          option.map(acceptableInput => {
            if (
              "documentCode" in acceptableInput &&
              "pageCode" in acceptableInput
            ) {
              return formatMessage("Zenid.Feedback.noMatchFound.backIdCard");
            } else if ("PossibleDocuments" in acceptableInput) {
              if (
                pipe(
                  acceptableInput.PossibleDocuments,
                  isArrayOfDocumentAcceptable(idCardCodesCZ)
                ) ||
                pipe(
                  acceptableInput.PossibleDocuments,
                  isArrayOfDocumentAcceptable(idCardCodesSK)
                )
              ) {
                return formatMessage("Zenid.Feedback.noMatchFound.frontIdCard");
              } else if (
                pipe(
                  acceptableInput.PossibleDocuments,
                  isArrayOfDocumentAcceptable([
                    ...idCardCodesSK,
                    ...passportCodesCZ,
                  ])
                ) ||
                pipe(
                  acceptableInput.PossibleDocuments,
                  isArrayOfDocumentAcceptable([
                    ...idCardCodesCZ,
                    ...passportCodesSK,
                  ])
                )
              ) {
                return formatMessage(
                  "Zenid.Feedback.noMatchFound.primaryFront"
                );
              } else if (
                pipe(
                  acceptableInput.PossibleDocuments,
                  isArrayOfDocumentAcceptable([
                    ...passportCodesSK,
                    ...drivingLicenseCodesSK,
                    ...drivingLicenseCodesCZ,
                  ])
                ) ||
                pipe(
                  acceptableInput.PossibleDocuments,
                  isArrayOfDocumentAcceptable([
                    ...passportCodesCZ,
                    ...drivingLicenseCodesCZ,
                    ...drivingLicenseCodesSK,
                  ])
                )
              ) {
                return formatMessage(
                  "Zenid.Feedback.noMatchFound.secondaryFront"
                );
              } else if (
                pipe(
                  acceptableInput.PossibleDocuments,
                  isArrayOfDocumentAcceptable([
                    ...drivingLicenseCodesSK,
                    ...drivingLicenseCodesCZ,
                  ])
                )
              ) {
                return formatMessage(
                  "Zenid.Feedback.noMatchFound.driverLicense"
                );
              }
            }
            return formatMessage("Zenid.Feedback.noMatchFound");
          }),
          option.getOrElse(
            constant(formatMessage("Zenid.Feedback.noMatchFound"))
          )
        );
      case Feedback.DocumentAlignCard:
        return formatMessage("Zenid.Feedback.alignCard");
      case Feedback.DocumentHoldSteady:
        return formatMessage("Zenid.Feedback.holdSteady");
      case Feedback.DocumentBlurry:
        return formatMessage("Zenid.Feedback.blurry");
      case Feedback.DocumentReflectionPresent:
        return formatMessage("Zenid.Feedback.reflectionPresent");
      case Feedback.DocumentDark:
        return formatMessage("Zenid.Feedback.dark");
      case Feedback.DocumentTiltLeftAndRight:
        return formatMessage("Zenid.Feedback.tiltLeftAndRight");
      case Feedback.DocumentTiltUpAndDown:
        return formatMessage("Zenid.Feedback.tiltUpAndDown");
      case Feedback.SelfieNoFaceFound:
        return formatMessage("Zenid.Feedback.noFaceFound");
      case Feedback.FaceLivenessLookAtMe:
        return formatMessage("Zenid.Feedback.LookAtMe");
      case Feedback.FaceLivenessTurnHead:
        return formatMessage("Zenid.Feedback.TurnHead");
      case Feedback.FaceLivenessSmile:
        return formatMessage("Zenid.Feedback.Smile");
      case Feedback.SelfieConfirmingFace:
        return formatMessage("Zenid.Feedback.ConfirmingFace");
      case Feedback.FaceLivenessHoldStill:
        return formatMessage("Zenid.Feedback.holdStill");
      case Feedback.SelfieBadFaceAngle:
        return formatMessage("Zenid.Feedback.badFaceAngle");
      case Feedback.SelfieBlurry:
        return formatMessage("Zenid.Feedback.blurry");
      case Feedback.SelfieDark:
        return formatMessage("Zenid.Feedback.dark");
      case Feedback.SelfieDecentred:
        return formatMessage("Zenid.Feedback.center");
      case Feedback.FaceLivenessBlurry:
        return formatMessage("Zenid.Feedback.blurry");
      case Feedback.FaceLivenessDark:
        return formatMessage("Zenid.Feedback.dark");
      case Feedback.FaceLivenessResetting:
        return formatMessage("Zenid.Feedback.livenessResetting");
      case Feedback.FaceLivenessDontSmile:
        return formatMessage("Zenid.Feedback.dontSmile");
      case Feedback.MsLivenessNotDone:
        return formatMessage("Zenid.Feedback.MsLivenessNotDone");
      default:
        return formatMessage("Zenid.Feedback.otherError");
    }
  }

  return formatFeedback;
}

/*class MyZenidBackendApi extends ZenidBackendApi {
  private init: (token: string) => Promise<string>;
  private upload: (
    sampleToUpload: SampleToUpload
  ) => Promise<UploadSampleBasicInfo>;
  private setupInfo: () => Promise<SetupInfo>;

  constructor(
    apiUrl: string,
    init: (token: string) => Promise<string>,
    upload: (sampleToUpload: SampleToUpload) => Promise<UploadSampleBasicInfo>,
    setupInfo: () => Promise<SetupInfo>
  ) {
    super(apiUrl);
    this.init = init;
    this.upload = upload;
    this.setupInfo = setupInfo;
  }

  initSdk(token: string): Promise<string> {
    return this.init(token);
  }

  uploadSample(sampleToUpload: SampleToUpload): Promise<UploadSampleBasicInfo> {
    return this.upload(sampleToUpload);
  }

  getSetupInfo(): Promise<SetupInfo> {
    return this.setupInfo();
  }
}*/
